import { Button, Text, Title } from '@components/v2/ui';
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import { ACLS } from '@config/constants/acls';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import {
	ActionIcon,
	Drawer,
	Flex,
	Group,
	Radio,
	RangeSlider,
	Stack,
} from '@mantine/core';
import useStore from '@store/index';
import { IconX } from '@tabler/icons-react';
import { checkAclFront } from '@utils/acl-front';
import { getCurrencyUnit } from '@utils/currency-units';
import { cloneDeep } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import isNil from 'lodash/isNil';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { useMeasurabilityProject } from '../../hooks/useMeasurabilityProjects';
import classes from './Filter.module.css';

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	'location.countryCode'?: { $in: string[] };
	technology?: string[];
	sellPrice_europertco2eq?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	[key: string]: unknown;
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	const { checkRole, role } = useAuthorization();
	const { t } = useTranslation();
	const { setMeasurabilityProjectsPage } = useStore();
	const { setMeasurabilityProjectsQueryParams } =
		useMeasurabilityProject();

	const customValuesStock = [
		0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 300,
		400, 500, 600, 700, 800, 900, 1000, 2000, 3000, 4000,
		5000, 6000, 7000, 8000, 9000, 10000,
	];

	const [
		valuePricePerTonCO2Slider,
		setPricePerTonCO2ValueSlider,
	] = useState<[number, number]>([0, 100]);
	const [stockIndices, setStockIndices] = useState<
		[number, number]
	>([0, customValuesStock.length - 1]);
	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	const findNearestIndex = (val: number) => {
		return customValuesStock.reduce(
			(prev, curr, idx) =>
				Math.abs(curr - val) <
				Math.abs(customValuesStock[prev] - val)
					? idx
					: prev,
			0
		);
	};

	const handleReset = () => {
		setMeasurabilityProjectsQueryParams({});
		setPricePerTonCO2ValueSlider([0, 100]);
		setStockIndices([0, customValuesStock.length - 1]);
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const { data: countryCodes } = useCountryCodes(
		'measurabilities'
	);
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const handleApplyFilters = () => {
		setMeasurabilityProjectsQueryParams(queryParams);
		setMeasurabilityProjectsPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));

			setPricePerTonCO2ValueSlider([
				value?.sellPrice_europertco2eq?.$gte ?? 0,
				value?.sellPrice_europertco2eq?.$lte ===
				1000000000000000
					? 100
					: (value?.sellPrice_europertco2eq?.$lte ?? 100),
			]);

			const gte =
				value?.['quantity.remaining_aco2']?.$gte ?? 0;
			const lteRaw =
				value?.['quantity.remaining_aco2']?.$lte;
			const lte =
				lteRaw === 10000000000 || isNil(lteRaw)
					? 10000
					: lteRaw;
			setStockIndices([
				findNearestIndex(gte),
				findNearestIndex(lte),
			]);
		}
	}, [opened, value]);

	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'16px'}>
					{checkAclFront(
						ACLS.FRONT.MEASURABILITIES.FILTER_STATUS,
						role as ROLES
					) &&
						!checkRole([
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
						]) && (
							<Radio.Group
								label={t('global.status')}
								value={
									queryParams?.active === true
										? 'active'
										: queryParams?.active === false
											? 'inActive'
											: 'all'
								}
								onChange={(val) => {
									if (val === 'all') {
										const { active, ...rest } =
											queryParams;
										setQueryParams(rest);
									} else {
										setQueryParams({
											...queryParams,
											active: val === 'active',
										});
									}
								}}
							>
								<Group mt="xs">
									<Radio
										value="all"
										label={t('global.all')}
									/>
									<Radio
										value="active"
										label={t('global.active')}
									/>
									<Radio
										value="inActive"
										label={t('global.inactive')}
									/>
								</Group>
							</Radio.Group>
						)}

					<MultiSelect
						placeholder={t('global.country')}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={filteredCountries}
						clearable
						size="lg"
						value={
							queryParams['location.countryCode']
								? (queryParams['location.countryCode']
										?.$in ?? [])
								: []
						}
						searchable
						onChange={(val) => {
							if (val.length === 0) {
								const _clone = cloneDeep(queryParams);
								delete _clone['location.countryCode'];
								setQueryParams(_clone);
								return;
							}
							setQueryParams({
								...queryParams,
								'location.countryCode': { $in: val },
							});
						}}
					/>

					<MultiSelect
						placeholder={t('global.technology')}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={PROJECT_TECHNOLOGIES}
						clearable
						size="lg"
						value={queryParams.technology ?? []}
						searchable
						onChange={(val) => {
							if (val.length === 0) {
								const _clone = cloneDeep(queryParams);
								delete _clone['technology'];
								setQueryParams(_clone);
								return;
							}
							setQueryParams({
								...queryParams,
								technology: val,
							});
						}}
					/>

					<Stack mt={'20px'} mb={'26px'}>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="button"
							mb={'12px'}
						>
							{t('global.pricePerTonCO2')}
						</Text>
						<RangeSlider
							labelAlwaysOn
							label={(val) =>
								val === 0 || val === 100
									? null
									: `${val} ${getCurrencyUnit('EUR')}`
							}
							color="var(--mantine-color-igluPrimary-9)"
							value={valuePricePerTonCO2Slider}
							marks={[
								{ value: 0, label: '0€' },
								{ value: 100, label: '100€' },
							]}
							min={0}
							max={100}
							step={1}
							onChange={(val) => {
								if (val[0] === 0 && val[1] === 100) {
									const _clone = cloneDeep(queryParams);
									delete _clone.sellPrice_europertco2eq;
									setQueryParams(_clone);
								} else {
									setQueryParams({
										...queryParams,
										sellPrice_europertco2eq: {
											$gte: val[0],
											$lte:
												val[1] === 100
													? 1000000000000000
													: val[1],
										},
									});
								}
								setPricePerTonCO2ValueSlider(val);
							}}
						/>
					</Stack>

					<Stack>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="button"
							mb={'12px'}
						>
							{t('global.availableStock')}
						</Text>
						<RangeSlider
							labelAlwaysOn
							color="var(--mantine-color-igluPrimary-9)"
							value={stockIndices}
							min={0}
							max={customValuesStock.length - 1}
							step={1}
							minRange={1}
							label={(idx) =>
								idx === 0 ||
								idx === customValuesStock.length - 1
									? null
									: `${customValuesStock[idx]} ${getUnitLabel('tco2')}`
							}
							marks={[
								{ value: 0, label: '0' },
								{ value: 10, label: '100' },
								{ value: 19, label: '1k' },
								{ value: 28, label: '+10k' },
							]}
							onChange={(indices) => {
								const gte = customValuesStock[indices[0]];
								const lteValue =
									customValuesStock[indices[1]];

								if (
									indices[0] === 0 &&
									indices[1] ===
										customValuesStock.length - 1
								) {
									const _clone = cloneDeep(queryParams);
									delete _clone[
										'quantity.remaining_aco2'
									];
									setQueryParams(_clone);
								} else {
									setQueryParams({
										...queryParams,
										'quantity.remaining_aco2': {
											$gte: gte,
											$lte:
												indices[1] ===
												customValuesStock.length - 1
													? 10000000000
													: lteValue,
										},
									});
								}
								setStockIndices(indices);
							}}
						/>
					</Stack>
				</Stack>

				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
					mt="xl"
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
